/**
 * settings/sectionRegistry.tsx — the settings section manifest (SET-001; reshaped by
 * Settings and the gear, Part 1 #5, 2026-10-29).
 *
 * One registry of every settings section: id, menu name, icon, group, the menu's
 * one-line "what's inside", whether it takes the full width, where it's used, and
 * the access gate. The SettingsPage shell draws its side menu, its Settings home
 * and its gate from this list, and the page-head gear (`HubSettingsGear`) checks the
 * same gate — so a section closed to someone is absent from the menu AND from every
 * gear that points at it.
 *
 * Groups and names (question 4 A, Michael 2026-10-29): You · Team · Planning · Work ·
 * Communication · Platform; "Communications" → "Phones & texts", "Inbox" → "Help desk
 * inbox", "AI Settings" → "AI". Every section keeps its address, /settings/<id>.
 */
import type { V2IconName } from '../v2/icons';

/** Every settings section id. `apiKeys` has no section of its own (its keys live in
 *  each integration's drawer); it is kept so an old `?tab=apiKeys` link lands on
 *  Integrations instead of failing. */
export type SettingsSectionId =
  | 'profile'
  | 'notifications'
  | 'connections'
  | 'quarterly'
  | 'annual'
  | 'users'
  | 'ai'
  | 'aiAgents'
  | 'integrations'
  | 'inbox'
  | 'projects'
  | 'sales'
  | 'support'
  | 'meetings'
  | 'communications'
  | 'customFields'
  | 'forms'
  | 'campaigns'
  | 'contacts'
  | 'timeTracking'
  | 'techAudit'
  | 'calls'
  | 'system'
  | 'apiKeys';

export const SETTINGS_GROUPS = ['You', 'Team', 'Planning', 'Work', 'Communication', 'Platform'] as const;
export type SettingsGroup = typeof SETTINGS_GROUPS[number];

/** The auth-derived facts a section's visibility gate reads. */
export interface SettingsGateContext {
  hasAdminSettings: boolean;
  hasAction: (action: string) => boolean;
}

export interface SettingsWhereUsed {
  /** The surface ("Call Crusher"). */
  what: string;
  /** What it reads from this section ("reads your calling hours"). */
  how: string;
}

export interface SettingsSection {
  id: SettingsSectionId;
  /** The menu name and the band's title. */
  label: string;
  icon: V2IconName;
  group: SettingsGroup;
  /** The menu's sub-line: what's inside, in a few words. */
  sub: string;
  /** One sentence for the band and the Settings home. */
  description: string;
  /** A list, table or builder: the full width, no side column. */
  wide?: boolean;
  /** "Where it's used", in the side column. */
  used?: SettingsWhereUsed[];
  /** Personal: everyone has it, and it is what someone without settings access lands on. */
  personal?: boolean;
  /** Whether the section is in the menu / openable for this auth. */
  visible: (ctx: SettingsGateContext) => boolean;
  /** The section draws its own band and side column (Settings · AI agents), so the frame
   *  draws neither: only the menu beside it. */
  ownsHead?: boolean;
}

const ALWAYS = () => true;
const ADMIN = (ctx: SettingsGateContext) => ctx.hasAdminSettings;
const ADMIN_AND = (action: string) => (ctx: SettingsGateContext) => ctx.hasAdminSettings && ctx.hasAction(action);

/**
 * The ordered manifest. Order within a group is the menu order; the group order is
 * `SETTINGS_GROUPS`. The gates are unchanged from SET-001 / the settings split
 * (D-04, gated = absent): a section whose routes need an action needs it here too.
 */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  // ── You ─────────────────────────────────────────────────────────────
  { id: 'profile', label: 'My profile', icon: 'user', group: 'You', personal: true, sub: 'Name, alerts, calling, email', description: 'Only you see these. They follow you to every device.', used: [{ what: 'Call Crusher', how: 'reads your calling hours and voicemail messages' }, { what: 'Email and Help Desk', how: 'add your signature to what you send' }], visible: ALWAYS },
  { id: 'notifications', label: 'Notifications', icon: 'bell', group: 'You', personal: true, sub: 'What reaches you', description: 'Choose what reaches you. Admins choose who gets each kind.', used: [{ what: 'The bell', how: 'every notification in the app' }], visible: ALWAYS },
  { id: 'connections', label: 'Connected accounts', icon: 'link', group: 'You', personal: true, sub: 'Google · Claude', description: 'Your Google accounts and your Claude connection.', used: [{ what: 'Bookings', how: 'reads your Google Calendar to offer times' }, { what: 'Email', how: 'syncs your own Gmail' }], visible: ALWAYS },
  // ── Team ────────────────────────────────────────────────────────────
  { id: 'users', label: 'Users & roles', icon: 'users', group: 'Team', wide: true, sub: 'People and what they can do', description: 'Who works here, their role, and what each role can do.', visible: ADMIN_AND('manage_users') },
  // ── Planning ────────────────────────────────────────────────────────
  { id: 'quarterly', label: 'Metric Hub', icon: 'scorecard', group: 'Planning', wide: true, sub: 'Metrics and their targets', description: 'Every metric, where its number comes from, and its target this quarter.', visible: ADMIN_AND('manage_metrics') },
  { id: 'annual', label: 'Annual goals', icon: 'rocks', group: 'Planning', wide: true, sub: 'The year’s goals', description: 'A goal for each metric for the year, and how the year is going.', visible: ADMIN_AND('manage_targets') },
  // ── Work ────────────────────────────────────────────────────────────
  { id: 'projects', label: 'Projects', icon: 'projects', group: 'Work', wide: true, sub: 'Stage playbook · emails', description: 'What the app does at each stage of a project, and where it waits for a person.', visible: ADMIN_AND('manage_project_config') },
  { id: 'sales', label: 'Sales', icon: 'pipeline', group: 'Work', sub: 'Stage playbook · Hedwig', description: 'The tasks each deal stage makes, Hedwig’s follow-ups, and the affiliate program.', used: [{ what: 'Deals', how: 'stage tasks and Hedwig’s messages' }, { what: 'Mission Control', how: 'everything Hedwig did' }], visible: ADMIN_AND('manage_sales_settings') },
  { id: 'support', label: 'Support', icon: 'ticket', group: 'Work', sub: 'Providers · CSAT', description: 'Support providers and the satisfaction survey after a ticket closes.', used: [{ what: 'Help Desk', how: 'providers on a ticket · the survey after it’s resolved' }], visible: ADMIN_AND('manage_support_config') },
  { id: 'meetings', label: 'Meetings', icon: 'calendar', group: 'Work', sub: 'Automated meetings', description: 'Meetings the app makes for you on a schedule.', used: [{ what: 'Weekly Kickoff', how: 'made every Monday' }], visible: ADMIN },
  { id: 'campaigns', label: 'Campaigns', icon: 'campaigns', group: 'Work', sub: 'Bounce pause · Gmail cap', description: 'The guards that protect your sending reputation.', used: [{ what: 'Campaigns', how: 'every send, unless a campaign sets its own' }], visible: ADMIN_AND('manage_campaigns') },
  { id: 'contacts', label: 'Contacts', icon: 'contacts', group: 'Work', wide: true, sub: 'The words the book uses', description: 'Statuses, tags, sources, roles and the import rules for the contact book.', visible: ADMIN_AND('manage_contacts') },
  { id: 'forms', label: 'Forms', icon: 'forms', group: 'Work', sub: 'Defaults · soon', description: 'Each form is set up in its own builder.', used: [{ what: 'Forms', how: 'each form’s own builder' }], visible: ADMIN },
  { id: 'timeTracking', label: 'Time tracking', icon: 'tracker', group: 'Work', sub: 'Categories · tasks', description: 'The tasks people can log time to.', used: [{ what: 'Time Tracker', how: 'the tasks you can log time to' }], visible: ADMIN_AND('manage_time_tracking') },
  { id: 'techAudit', label: 'Tech audit', icon: 'sops', group: 'Work', wide: true, sub: 'Shared lists', description: 'The shared lists every audit picks from: approve, merge, retire.', visible: ADMIN_AND('manage_operations_settings') },
  { id: 'calls', label: 'Lord of the Notes', icon: 'phone', group: 'Work', sub: 'Sync · who sees · share links', description: 'How Lord of the Notes runs: the pull from Fathom, who sees which calls, the sorting rules and every share link.', used: [{ what: 'Lord of the Notes', how: 'every call, how it’s sorted and who sees it' }, { what: 'Time Tracker', how: 'time entries made from calls' }], visible: (ctx) => ctx.hasAction('manage_fathom_settings') },
  // ── Communication ───────────────────────────────────────────────────
  { id: 'inbox', label: 'Help desk inbox', icon: 'helpdesk', group: 'Communication', wide: true, sub: 'support@ · rules · routing', description: 'The shared support inbox: its connection, rules and who each client’s email goes to.', used: [{ what: 'Help Desk', how: 'the shared inbox and its tickets' }], visible: ADMIN },
  { id: 'communications', label: 'Phones & texts', icon: 'comms', group: 'Communication', sub: 'Lines · booking texts', description: 'Phone lines, booking texts and routing rules.', used: [{ what: 'Bookings', how: 'confirmation texts to the booker and hosts' }, { what: 'Phone', how: 'every call and text on these lines' }], visible: ADMIN },
  // ── Platform ────────────────────────────────────────────────────────
  { id: 'integrations', label: 'Integrations', icon: 'globe', group: 'Platform', sub: 'Keys and connections', description: 'The services the app connects to, and their keys.', used: [{ what: 'Lord of the Notes', how: 'Fathom recordings and grading' }, { what: 'Phones & texts', how: 'Twilio keys' }], visible: ADMIN_AND('manage_integrations') },
  { id: 'ai', label: 'AI', icon: 'sparkle', group: 'Platform', wide: true, sub: 'Models · spend · AI staff', description: 'Which AI models run, what they cost, and the AI staff.', visible: ADMIN_AND('manage_ai_settings') },
  // Mission Control 2.0 PR 4 (Entry 536): each agent's name and painted look. Gated on its
  // own key alone (Super Admin by seed), D-04 gated = absent. It draws its own band and
  // side column, so the frame draws only the menu beside it (`ownsHead`).
  { id: 'aiAgents', label: 'AI agents', icon: 'spark', group: 'Platform', wide: true, ownsHead: true, sub: 'Names and looks', description: 'Each AI agent’s name and the painted creature it wears.', visible: (ctx) => ctx.hasAction('manage_agent_appearance') },
  { id: 'customFields', label: 'Custom fields', icon: 'sliders', group: 'Platform', wide: true, sub: 'Every field', description: 'Every field on every kind of record.', visible: ADMIN },
  // System is the operational read: gated on view_system_health alone (portal G0.9).
  { id: 'system', label: 'System', icon: 'desktop', group: 'Platform', wide: true, sub: 'Live health', description: 'How the app is running right now. Read-only.', visible: (ctx) => ctx.hasAction('view_system_health') },
];

export const SETTINGS_SECTION_BY_ID: Record<string, SettingsSection> = Object.fromEntries(
  SETTINGS_SECTIONS.map((s) => [s.id, s]),
);

/** Old section addresses that now land elsewhere. */
export const SETTINGS_SECTION_ALIASES: Record<string, SettingsSectionId> = {
  apiKeys: 'integrations',
  'custom-fields': 'customFields',
  'time-tracking': 'timeTracking',
  'tech-audit': 'techAudit',
};

/** The sections this auth may see, in manifest order. */
export function visibleSettingsSections(ctx: SettingsGateContext): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((s) => s.visible(ctx));
}

/** The ids this auth may open, in manifest order. */
export function allowedSettingsSectionIds(ctx: SettingsGateContext): SettingsSectionId[] {
  return visibleSettingsSections(ctx).map((s) => s.id);
}
